import { Box, Button, Container, FormControl, InputLabel, MenuItem, SelectChangeEvent, Typography } from "@mui/material";
import { TrackedSelect } from "src/theme/StyledFields";
import { TrackedTextField } from "src/theme/StyledFields";
import React, { useMemo, useState } from "react";
import ApiClient from "../config/ApiClient";
import HeroSection from "../theme/HeroSection";
import { useRoleLabels } from "../hooks/useRoleLabels";

const Contact: React.FC = () => {
    const { getRoleLabel } = useRoleLabels();
    const roleOptions = useMemo(
        () => [
            { value: "athlete", label: getRoleLabel("athlete") },
            { value: "coach", label: getRoleLabel("coach") },
            { value: "guardian", label: getRoleLabel("parent") },
            { value: "organization", label: "Organization" },
        ],
        [getRoleLabel]
    );
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState<string>("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const handleRoleChange = (event: SelectChangeEvent<string>) => {
        setRole(event.target.value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitSuccess(false);
        setSubmitting(true);
        try {
            const res = await ApiClient.post<{ detail?: string }>("/api/contact", {
                name: name.trim(),
                email: email.trim(),
                role,
                message: message.trim()
            });
            if (res.success) {
                setSubmitSuccess(true);
                setName("");
                setEmail("");
                setRole("");
                setMessage("");
            } else {
                setSubmitError(res.error ?? "Something went wrong. Please try again.");
            }
        } catch {
            setSubmitError("Unable to send your message. Please try again later.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box>
            <HeroSection minHeight="40vh" showWave={true} imageUrl="/images/SeanValentine-HGP.jpg">
                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Box sx={{ maxWidth: 640 }}>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                color: "white",
                                fontWeight: "bold",
                                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" }
                            }}
                        >
                            Connect with us
                        </Typography>
                    </Box>
                </Container>
            </HeroSection>

            <Container maxWidth="sm" sx={{ py: 6 }}>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    data-attr="contact-form_submit"
                >
                    {submitSuccess && (
                        <Typography color="success.main" sx={{ fontWeight: 500 }}>
                            Thank you. Your message has been sent.
                        </Typography>
                    )}
                    {submitError && (
                        <Typography color="error" sx={{ fontWeight: 500 }}>
                            {submitError}
                        </Typography>
                    )}
                    <TrackedTextField
                        label="Name"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={submitting}
                        data-attr="contact-form_field-name"
                    />
                    <TrackedTextField
                        label="Email"
                        type="email"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={submitting}
                        data-attr="contact-form_field-email"
                    />
                    <FormControl fullWidth required disabled={submitting}>
                        <InputLabel id="contact-role-label">Select your role</InputLabel>
                        <TrackedSelect
                            labelId="contact-role-label"
                            id="contact-role"
                            value={role}
                            label="Select your role"
                            onChange={handleRoleChange}
                            data-attr="contact-form_field-role"
                        >
                            {roleOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TrackedSelect>
                    </FormControl>
                    <TrackedTextField
                        label="Message"
                        fullWidth
                        multiline
                        rows={5}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="How can we help?"
                        disabled={submitting}
                        data-attr="contact-form_field-message"
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={submitting}
                        sx={{ alignSelf: "flex-start", px: 4, py: 1.5, fontWeight: "bold" }}
                        data-attr="contact-form_submit-button"
                    >
                        {submitting ? "Sending…" : "Send message"}
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default Contact;
